import { createSelector } from "reselect";
import { mean, stdev } from "stats-lite";
import _ from "underscore";

import type { MedicalPlanPrice, CombinedFilters } from "Utils/types";

export type PlanLoaderState = {
  loadingPlans: boolean;
  allPlans: Array<MedicalPlanPrice>;
  sort: string;
  savedPlanIDs: Array<string>;
  favoritePlanIDs: Array<string>;
  filters: CombinedFilters;
  medicalPlanEnrolledId: string | null | undefined;
  medicalPlanRenewalId: string | null | undefined;
  isShowingExtraPlans: boolean;
};
export type PlanDeviationProps = Record<string, number> | {};
export const getSortConfig = (state: PlanLoaderState): string => state.sort;
export const getSavedPlanIDs = (state: PlanLoaderState): Array<string> => state.savedPlanIDs;
export const isPlanCompared: (state: PlanLoaderState, planId: string) => boolean = createSelector(
  [getSavedPlanIDs, (_state, planId) => planId],
  (savedPlanIDs, planId) => savedPlanIDs.includes(planId),
);
export const isLoadingPlans = (state: PlanLoaderState): boolean => state.loadingPlans;
export const isShowingExtraPlans = (state: PlanLoaderState): boolean => state.isShowingExtraPlans;
export const getPlans = (state: PlanLoaderState): Array<MedicalPlanPrice> => state.allPlans;
export const getSavedPlans: (arg0: PlanLoaderState) => Array<MedicalPlanPrice> = createSelector(
  [getSavedPlanIDs, getPlans],
  (savedPlanIDs: Array<string>, plans: Array<MedicalPlanPrice>): Array<MedicalPlanPrice> =>
    savedPlanIDs.map((id) =>
      _.findWhere(plans, {
        id,
      }),
    ),
);
export const getLowestCostPlan: (arg0: PlanLoaderState) => MedicalPlanPrice | null | undefined = createSelector(
  [getPlans],
  (plans: Array<MedicalPlanPrice>): MedicalPlanPrice => plans.slice(0).sort((plan1, plan2) => plan1.average_monthly_premium - plan2.average_monthly_premium)[0],
);
export const calculatePlanDeviationProps = (plans: Array<MedicalPlanPrice>): PlanDeviationProps => {
  const keys: Array<string> = Object.keys(plans.reduce((memo: Record<string, any>, plan: MedicalPlanPrice) => Object.assign(memo, plan.estimated_costs), {}));
  return keys.reduce((stats: Record<string, any>, key: string) => {
    stats[`mean_${key}`] = mean(plans.map((p) => p.estimated_costs[key]));
    stats[`stdev_${key}`] = stdev(plans.map((p) => p.estimated_costs[key]));
    return stats;
  }, {});
};
export const getPlanDeviationProps: (arg0: PlanLoaderState) => PlanDeviationProps = createSelector([getPlans], calculatePlanDeviationProps);
export const getMedicalPlanEnrolledId = (state: PlanLoaderState): string | null | undefined => state.medicalPlanEnrolledId;
export const getMedicalPlanRenewalId = (state: PlanLoaderState): string | null | undefined => state.medicalPlanRenewalId;
export const getPinnedPlans: (arg0: PlanLoaderState) => Array<MedicalPlanPrice> = createSelector(
  [getPlans, getMedicalPlanEnrolledId, getMedicalPlanRenewalId],
  (plans: Array<MedicalPlanPrice>, medicalPlanId: string | null | undefined, renewalPlanId: string | null | undefined): Array<MedicalPlanPrice> => {
    const pinnedPlansIds = new Set([medicalPlanId, renewalPlanId]);
    return plans.filter((p) => pinnedPlansIds.has(p.id));
  },
);
export function getRecommendationMode(state: PlanLoaderState): string {
  return state.filters.recommendation_mode;
}
export const getLowestCostPPO: (arg0: PlanLoaderState) => MedicalPlanPrice | null | undefined = createSelector([getPlans], (plans): MedicalPlanPrice => {
  const ppoPlans = plans.filter((plan) => plan.plan_type === "PPO" || plan.plan_type === "POS");
  return ppoPlans.slice(0).sort((plan1, plan2) => plan1.average_monthly_premium - plan2.average_monthly_premium)[0];
});
export const getPlanById = (state: PlanLoaderState, id: string): MedicalPlanPrice | null | undefined => getPlans(state).find((plan) => plan.id === id);
